import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import { somethingWrongNotification } from '../../notifier/notifierActions';
import { changeConfirmed, changeGenieConfirmed } from '../consumerReducer';
import buildConsumerRequestBody from '../../../utils/buildConsumerRequestBody';
import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE } from '../../../constants';
import {
  cancelEvaluationByType,
  setEvalAbortController,
  updateEvaluatedGeneratedMessages,
} from '../../genie-go/genieGoReducer';

const GET_CONSUMER_GENIE_GO_DATA = 'consumer/genieGo/GET_CONSUMER_GENIE_GO_DATA';
const CONSUMER_GENIE_GO_PREDICT_RESPONSE = 'consumer/genieGo/CONSUMER_GENIE_GO_PREDICT_RESPONSE';
const CONSUMER_GENIE_GO_REPLY = 'consumer/genieGo/CONSUMER_GENIE_GO_REPLY';
const CONSUMER_GENIE_GO_GENERATE_MESSAGES = 'consumer/genieGo/CONSUMER_GENIE_GO_GENERATE_MESSAGES';
const CONSUMER_GENIE_GO_RESET_SESSION = 'consumer/genieGo/CONSUMER_GENIE_GO_RESET_SESSION';
const EVALUATE_GENERATED_MESSAGES = 'consumer/genieGo/EVALUATE_GENERATED_MESSAGES';

export const getConsumerGenieGoData = createAsyncThunk(
  GET_CONSUMER_GENIE_GO_DATA,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        axios.post(`/api/consumer/genie2go`, {
          projectName,
          projectPassword,
        }),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          dispatch(changeConfirmed(false));
          return rejectWithValue(null);
        },
      },
    );

    dispatch(changeGenieConfirmed({ confirmed: true, projectName, projectPassword }));

    return response.data;
  },
);

export const predictConsumerGenieGoResponse = createAsyncThunk(
  CONSUMER_GENIE_GO_PREDICT_RESPONSE,
  async ({ message, url }, { rejectWithValue, getState }) => {
    const requestBody = buildConsumerRequestBody(getState().consumer.genie, {
      message,
      url,
    });

    const response = await sendRequest(() => axios.post(`/api/consumer/genie2go/predict/messages`, requestBody), {
      onError: (e) => rejectWithValue(e),
    });

    return response.data;
  },
);

export const generateConsumerGenieGoMessages = createAsyncThunk(
  CONSUMER_GENIE_GO_GENERATE_MESSAGES,
  async ({ values }, { rejectWithValue, dispatch, getState }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!assistantId || !url) {
      return rejectWithValue(null);
    }

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const requestBody = buildConsumerRequestBody(getState().consumer.genie, {
      assistantId,
      byPass,
      focusOn,
      url,
      fileText,
    });

    const response = await sendRequest(() => axios.post(`/api/consumer/genie2go/url`, requestBody), {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    });

    dispatch(evaluateGeneratedMessagesConsumer({ values, messages: response.data.messages }));

    return response.data;
  },
);

export const replyConsumerGenieGo = createAsyncThunk(
  CONSUMER_GENIE_GO_REPLY,
  async ({ reply, assistantId, generateFormValues }, { rejectWithValue, getState, dispatch }) => {
    const storedThreadId = getState().genieGo.threadId;

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const requestBody = buildConsumerRequestBody(getState().consumer.genie, {
      reply,
      assistantId,
      threadId: storedThreadId,
    });

    const response = await sendRequest(() => axios.post(`/api/consumer/genie2go/reply`, requestBody), {
      onError: (e) => rejectWithValue(e),
    });

    dispatch(evaluateGeneratedMessagesConsumer({ values: generateFormValues, messages: response.data.messages }));

    return response.data;
  },
);

export const resetGenieGoSession = createAsyncThunk(
  CONSUMER_GENIE_GO_RESET_SESSION,
  async (_, { rejectWithValue, getState }) => {
    const threadId = getState().genieGo.threadId;

    if (!threadId) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(() => axios.post(`/api/consumer/open-ai/reset?threadId=${threadId}`), {
      onError: (e) => rejectWithValue(e),
    });

    return response.data;
  },
);

export const evaluateGeneratedMessagesConsumer = createAsyncThunk(
  EVALUATE_GENERATED_MESSAGES,
  async ({ values, messages }, { rejectWithValue, dispatch, getState }) => {
    const { url } = values;

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.GENERATED }));

    const requestBody = buildConsumerRequestBody(getState().consumer.genie, {
      messages,
      url,
    });

    try {
      const response = await sendRequest(() => axios.post(`/api/consumer/genie2go/predict/bunch`, requestBody), {
        onError: (e) => {
          throw new Error(e);
        },
      });

      dispatch(updateEvaluatedGeneratedMessages(response.data));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.GENERATED }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      return rejectWithValue(error.message);
    }
  },
);
